import { motion } from "framer-motion";
import { PRACTICE } from "@/lib/data";
import { useContent } from "@/lib/api";

const PRACTICE_IMG = "https://images.pexels.com/photos/30379884/pexels-photo-30379884.jpeg";

const LEADERSHIP_FALLBACK = [
  {
    name: "Andrew Regenberg",
    role: "Vice President of Finance",
    bio: "Oversees RCCD's financial operations — capital strategy, equity transactions, and the underwriting that supports the firm's specific-plan and Executive Management work.",
    initials: "AR",
    confirmed: true,
  },
  {
    name: "Executive Leadership",
    role: "Specific Plans · Entitlement",
    bio: "Senior plan authors and entitlement leads driving the firm's five adopted specific plans across Ontario and Chino.",
    initials: "EL",
    confirmed: false,
  },
  {
    name: "Executive Management",
    role: "NMC Program",
    bio: "Coordinates the New Model Colony consortium — schedule alignment, infrastructure delivery, and city-side relationships in the 4,000-acre program.",
    initials: "EM",
    confirmed: false,
  },
  {
    name: "Agency Relations",
    role: "City & County Liaison",
    bio: "Direct working relationships with Planning, Engineering, Building, Finance, and Utilities departments across Southern California.",
    initials: "AR",
    confirmed: false,
  },
];

// Leadership + practice areas. People listed here are real and named with permission.
export const Team = () => {
  const leadership = useContent("team", LEADERSHIP_FALLBACK);
  return (
    <section id="practice" className="py-24 lg:py-40 bg-ink text-paper grain">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        {/* Leadership */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 lg:pb-24 border-b border-paper/15">
          <div className="lg:col-span-5">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">Section · 08 — Leadership</span>
            <h2 className="font-display text-4xl lg:text-6xl mt-4 leading-[1.02] tracking-[-0.03em]">
              The people<br/><em className="font-light text-paper/75">behind the work.</em>
            </h2>
            <p className="mt-6 text-paper/70 text-base lg:text-lg max-w-md leading-relaxed">
              A small, senior team — direct access from the first conversation through delivery.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            {leadership.map((person, i) => (
              <motion.article
                key={person.name + i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`group border p-7 lg:p-8 transition-all flex flex-col ${
                  person.confirmed
                    ? 'bg-paper/[0.05] border-paper/20 hover:border-clay hover:bg-paper/[0.08]'
                    : 'bg-paper/[0.02] border-dashed border-paper/15 hover:border-paper/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  {person.image ? (
                    <img
                      src={person.image}
                      alt={person.name}
                      className="h-14 w-14 rounded-full object-cover border border-clay/50 transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className={`h-14 w-14 rounded-full border flex items-center justify-center font-display text-xl transition-transform group-hover:scale-105 ${
                      person.confirmed
                        ? 'bg-clay/20 border-clay/50 text-primary-glow'
                        : 'bg-paper/[0.04] border-paper/20 text-paper/55'
                    }`}>
                      {person.initials}
                    </div>
                  )}
                  <span className="mono text-[10px] uppercase tracking-[0.22em] text-paper/40">
                    {person.confirmed ? String(i+1).padStart(2,'0') : 'TBA'}
                  </span>
                </div>
                <h3 className={`font-display text-2xl mt-5 leading-tight tracking-[-0.01em] ${
                  person.confirmed ? 'text-paper' : 'text-paper/75'
                }`}>
                  {person.name}
                </h3>
                <div className={`mt-1 mono text-[10px] uppercase tracking-[0.22em] ${
                  person.confirmed ? 'text-clay' : 'text-paper/45'
                }`}>{person.role}</div>
                <p className={`mt-4 text-sm leading-relaxed ${
                  person.confirmed ? 'text-paper/75' : 'text-paper/55'
                }`}>{person.bio}</p>
                {!person.confirmed && (
                  <div className="mt-5 mono text-[9px] uppercase tracking-[0.25em] text-paper/35">
                    Profile coming soon
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        </div>

        {/* Practice areas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-16 lg:pt-24">
          <div className="lg:col-span-5">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">How we work</span>
            <h3 className="font-display text-3xl lg:text-5xl mt-4 leading-[1.02] tracking-[-0.02em]">
              Four practice<br/><em className="font-light text-paper/75">areas.</em>
            </h3>
            <p className="mt-6 text-paper/70 text-base max-w-md leading-relaxed">
              Over 35 years, RCCD's practice has compounded across four disciplines — and every project draws on all of them.
            </p>

            <div className="mt-10 divide-y divide-paper/15 border-y border-paper/15">
              {PRACTICE.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="py-6 group"
                >
                  <div className="flex items-baseline gap-5">
                    <span className="mono text-[10px] tracking-[0.22em] text-paper/40 w-6">{String(i+1).padStart(2,'0')}</span>
                    <div className="flex-1">
                      <div className="font-display text-xl text-paper group-hover:text-primary-glow transition-colors">{p.title}</div>
                      <div className="mt-2 text-sm text-paper/70 leading-relaxed">{p.detail}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="aspect-[4/5] overflow-hidden relative">
              <video
                src="https://customer-assets.emergentagent.com/job_interactive-showcase-79/artifacts/jxvfqyno_lots-of-cranes-at-the-evening-construction-time-lapse-SBV-346638104-HD.mp4"
                poster={PRACTICE_IMG}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="h-full w-full object-cover grayscale-[0.15]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-ink/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-paper/70">Southern California · Ongoing</div>
                  <div className="font-display text-paper text-2xl mt-1">Specific plan coordination</div>
                </div>
                <div className="hidden sm:block mono text-[10px] tracking-[0.22em] text-paper/50">Ontario / Chino</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
